import React from "react";
import { NavLink } from "react-router-dom";
import { Memory } from "../../core/Memory";
import SocketEngine from "../../core/SocketEngine";
import { clearSession } from "../../core/session";
import strings from "../../core/translate";
import {
  getGarageBrowseSections,
  getGarageSections,
  GARAGE_SECTION_GROUPS,
} from "../../shared/garage-sections";
import logo from "assets/img/logo.png";
import NotificationBell from "./NotificationBell";

// the one "Users Management" entry a garage login keeps — see routes.js
const CUSTOMER_USERS_PATH = "/customer_user/:selectedMerchant/users";

/**
 * Horizontal navigator: the menu runs across the top instead of down the side.
 * Collapsible route groups become dropdowns, single routes become links, and
 * everything narrower than a laptop folds into a drawer.
 */
class TopNav extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      openMenu: null, // group whose dropdown is open
      menuPos: null, // where its dropdown floats, measured from the button
      drawerOpen: false, // mobile drawer
      openDrawerGroup: null,
      canScrollLeft: false,
      canScrollRight: false,
      lang: Memory.getItem("lang"),
    };
    this.rootRef = React.createRef();
    this.scrollerRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleOutsideClick);
    document.addEventListener("keydown", this.handleEscape);
    window.addEventListener("resize", this.updateScrollState);
    this.updateScrollState();
    this.scrollActiveIntoView();
  }

  componentDidUpdate(prevProps) {
    // a navigation closes whatever was open
    if (prevProps.location.pathname !== this.props.location.pathname) {
      this.setState({ openMenu: null, drawerOpen: false });
    }
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
    document.removeEventListener("keydown", this.handleEscape);
    window.removeEventListener("resize", this.updateScrollState);
  }

  // ---- horizontal scrolling ------------------------------------------------

  updateScrollState = () => {
    const el = this.scrollerRef.current;
    if (!el) return;
    const canScrollLeft = el.scrollLeft > 1;
    const canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    if (
      canScrollLeft !== this.state.canScrollLeft ||
      canScrollRight !== this.state.canScrollRight
    ) {
      this.setState({ canScrollLeft, canScrollRight });
    }
  };

  handleScroll = () => {
    // a floating dropdown would drift away from its button
    if (this.state.openMenu !== null) {
      this.setState({ openMenu: null, menuPos: null });
    }
    this.updateScrollState();
  };

  // a vertical wheel over the bar scrolls the sections sideways
  handleWheel = (event) => {
    const el = this.scrollerRef.current;
    if (!el || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
    if (el.scrollWidth <= el.clientWidth) return;
    el.scrollLeft += event.deltaY;
    event.preventDefault();
  };

  scrollBy = (direction) => {
    const el = this.scrollerRef.current;
    if (!el) return;
    const step = Math.max(160, Math.round(el.clientWidth * 0.6));
    const left = el.scrollLeft + direction * step;
    if (el.scrollTo) {
      el.scrollTo({ left, behavior: "smooth" });
    } else {
      el.scrollLeft = left;
    }
  };

  scrollActiveIntoView() {
    const el = this.scrollerRef.current;
    if (!el) return;
    const active = el.querySelector(".topnav__link.is-active");
    if (active && active.offsetLeft + active.offsetWidth > el.clientWidth) {
      el.scrollLeft = active.offsetLeft - 16;
    }
  }

  handleOutsideClick = (event) => {
    if (this.rootRef.current && !this.rootRef.current.contains(event.target)) {
      this.setState({ openMenu: null });
    }
  };

  handleEscape = (event) => {
    if (event.key === "Escape") {
      this.setState({ openMenu: null, drawerOpen: false });
    }
  };

  userScopes() {
    return (sessionStorage.getItem("scopes") || "")
      .split(",")
      .map((scope) => scope.trim());
  }

  /**
   * Hidden routes, redirects, then scopes. menu: false hides a route outright:
   * the old sidebar let a route with `allow` back in even when it was marked
   * hidden, so hiding a section by flag alone did nothing.
   */
  allowed(route) {
    if (route.redirect) return false;
    if (route.menu === false) return false;
    if (route.allow) {
      const scopes = this.userScopes();
      return route.allow.some(
        (scope) => scope && scopes.includes(scope.toLowerCase())
      );
    }
    return !!route.menu;
  }

  visibleRoutes() {
    const base = (this.props.routes || [])
      .filter((route) => this.allowed(route))
      .map((route) =>
        route.collapse
          ? { ...route, views: route.views.filter((view) => this.allowed(view)) }
          : route
      )
      .filter((route) => !route.collapse || route.views.length > 0);

    const garageBranchId = sessionStorage.getItem("garageBranchId");
    if (!garageBranchId) return base;

    return this.applyGarageLoginNav(base, garageBranchId);
  }

  // A garage login has exactly one garage: the garages list is swapped for
  // that garage's own sections, shown directly instead of behind a card.
  // Staff/setup move under the same "Setup" dropdown as payment methods
  // instead of sitting at the top level. "Users Management" collapses to a
  // single "Customers" link: roles and user access are the platform admin's,
  // not the garage's.
  applyGarageLoginNav(base, branchId) {
    // The garage's services and sub-services are its price list: set up
    // rather than browsed, but a garage login has no other way to reach them,
    // so they sit under "Setup" with staff and garage setup.
    const SETUP_KEYS = ["services", "workers", "garage-setup"];
    // The top level is the day's work, so it carries only the sections you
    // browse to — see NOT_BROWSED in shared/garage-sections.
    const primary = getGarageBrowseSections(branchId).filter(
      (s) => !SETUP_KEYS.includes(s.key)
    );
    const setupExtras = getGarageSections(branchId)
      .filter((s) => SETUP_KEYS.includes(s.key))
      .map((s) => this.toGarageLink(s));
    const garageRoutes = this.groupGarageSections(primary);

    let mergedSetup = false;
    const withGarageRoutes = base.flatMap((route) => {
      if (route.hideForGarageLogin) return garageRoutes;
      if (route.state === "usersManagement") return this.customersLink(route);
      if (route.state === "setup" && setupExtras.length > 0) {
        mergedSetup = true;
        return [{ ...route, views: [...route.views, ...setupExtras] }];
      }
      return [route];
    });

    if (mergedSetup || setupExtras.length === 0) return withGarageRoutes;

    // the "Setup" dropdown isn't visible this session — give it its own so
    // staff/setup stay reachable
    return [
      ...withGarageRoutes,
      {
        name: strings.setup,
        icon: "nc-icon nc-settings-gear-65",
        collapse: true,
        state: "garage-login-setup",
        views: setupExtras,
      },
    ];
  }

  // Only the customer list survives from "Users Management", flattened out of
  // the dropdown and labelled "Customers". Nothing is left when this session
  // may not open it.
  customersLink(route) {
    const customers = (route.views || []).find(
      (view) => view.path === CUSTOMER_USERS_PATH
    );
    return customers ? [{ ...customers, name: strings.customers }] : [];
  }

  toGarageLink(section) {
    return {
      path: section.path,
      layout: "",
      name: section.label,
      icon: section.icon,
    };
  }

  // Related sections (e.g. Appointments + Appointment Settings) collapse
  // into one dropdown, the same way General Services groups its items. A
  // group left with only one member is shown as a flat link instead.
  groupGarageSections(sections) {
    const order = [];
    const groups = {};

    sections.forEach((section) => {
      if (!section.group) {
        order.push({ solo: section });
        return;
      }
      if (!groups[section.group]) {
        groups[section.group] = { members: [] };
        order.push(groups[section.group]);
      }
      groups[section.group].members.push(section);
    });

    return order.map((entry) => {
      if (entry.solo || entry.members.length === 1) {
        return this.toGarageLink(entry.solo || entry.members[0]);
      }
      const groupId = entry.members[0].group;
      const meta = GARAGE_SECTION_GROUPS[groupId] || {};
      return {
        name: meta.label ? meta.label() : groupId,
        icon: meta.icon || entry.members[0].icon,
        collapse: true,
        state: `garage-${groupId}`,
        views: entry.members.map((s) => this.toGarageLink(s)),
      };
    });
  }

  isGroupActive(route) {
    const { pathname } = this.props.location;
    return (route.views || []).some(
      (view) => pathname.indexOf(view.layout + view.path.split("/:")[0]) !== -1
    );
  }

  isRouteActive(route) {
    const target = route.layout + route.path.split("/:")[0];
    return this.props.location.pathname.indexOf(target) !== -1;
  }

  setLanguage(next) {
    strings.setLanguage(next);
    Memory.setItem("lang", next);
    window.location.reload();
  }

  handleLogout = () => {
    SocketEngine.disconnect();
    clearSession();
    this.props.history.push("/auth/login");
  };

  openGroup(key, event) {
    if (this.state.openMenu === key) {
      this.setState({ openMenu: null, menuPos: null });
      return;
    }
    // the menu is fixed, not nested: a scrolling container would clip it
    const rect = event.currentTarget.getBoundingClientRect();
    const width = 240;
    const left = Math.min(rect.left, window.innerWidth - width - 12);
    this.setState({
      openMenu: key,
      menuPos: { top: Math.round(rect.bottom + 8), left: Math.round(Math.max(12, left)) },
    });
  }

  renderGroup(route, key) {
    const { openMenu, menuPos } = this.state;
    const open = openMenu === key;
    const active = this.isGroupActive(route);

    return (
      <li className="topnav__item" key={key}>
        <button
          type="button"
          className={`topnav__link${active ? " is-active" : ""}${
            open ? " is-open" : ""
          }`}
          aria-expanded={open}
          onClick={(event) => this.openGroup(key, event)}
        >
          {route.icon && <i className={route.icon} />}
          <span>{route.name}</span>
          <i className="topnav__caret nc-icon nc-minimal-down" />
        </button>

        {open && (
          <ul className="topnav__menu" style={menuPos || undefined}>
            {route.views.map((view, index) => (
              <li key={index}>
                <NavLink
                  to={view.layout + view.path}
                  className="topnav__menu-link"
                  activeClassName="is-active"
                  onClick={() => this.setState({ openMenu: null, menuPos: null })}
                >
                  {view.icon && <i className={view.icon} />}
                  <span>{view.name}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  }

  renderLink(route, key) {
    return (
      <li className="topnav__item" key={key}>
        <NavLink
          to={route.layout + route.path}
          className={`topnav__link${this.isRouteActive(route) ? " is-active" : ""}`}
        >
          {route.icon && <i className={route.icon} />}
          <span>{route.name}</span>
        </NavLink>
      </li>
    );
  }

  renderDrawer(routes) {
    const { openDrawerGroup } = this.state;

    return (
      <div className="topnav__drawer">
        <ul>
          {routes.map((route, key) =>
            route.collapse ? (
              <li key={key}>
                <button
                  type="button"
                  className="topnav__drawer-group"
                  onClick={() =>
                    this.setState({
                      openDrawerGroup: openDrawerGroup === key ? null : key,
                    })
                  }
                >
                  {route.icon && <i className={route.icon} />}
                  <span>{route.name}</span>
                  <i className="topnav__caret nc-icon nc-minimal-down" />
                </button>
                {openDrawerGroup === key && (
                  <ul className="topnav__drawer-sub">
                    {route.views.map((view, index) => (
                      <li key={index}>
                        <NavLink
                          to={view.layout + view.path}
                          activeClassName="is-active"
                          onClick={() => this.setState({ drawerOpen: false })}
                        >
                          {view.name}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ) : (
              <li key={key}>
                <NavLink
                  to={route.layout + route.path}
                  className="topnav__drawer-group"
                  onClick={() => this.setState({ drawerOpen: false })}
                >
                  {route.icon && <i className={route.icon} />}
                  <span>{route.name}</span>
                </NavLink>
              </li>
            )
          )}
        </ul>
      </div>
    );
  }

  render() {
    const { lang, drawerOpen, openMenu, canScrollLeft, canScrollRight } =
      this.state;
    const routes = this.visibleRoutes();

    return (
      <header className="topnav" ref={this.rootRef}>
        <div className="topnav__bar">
          <button
            type="button"
            className="topnav__burger"
            aria-label="Menu"
            onClick={() => this.setState({ drawerOpen: !drawerOpen })}
          >
            <span />
            <span />
            <span />
          </button>

          <div className="topnav__brand">
            <img src={logo} alt="logo" />
          </div>

          <nav
            className={`topnav__nav${
              canScrollLeft ? " has-left" : ""
            }${canScrollRight ? " has-right" : ""}`}
          >
            <button
              type="button"
              className="topnav__scroll topnav__scroll--left"
              aria-label="Scroll left"
              tabIndex={canScrollLeft ? 0 : -1}
              onClick={() => this.scrollBy(-1)}
            >
              <i className="nc-icon nc-minimal-left" />
            </button>

            <ul
              className="topnav__list"
              ref={this.scrollerRef}
              onScroll={this.handleScroll}
              onWheel={this.handleWheel}
            >
              {routes.map((route, key) =>
                route.collapse
                  ? this.renderGroup(route, key)
                  : this.renderLink(route, key)
              )}
            </ul>

            <button
              type="button"
              className="topnav__scroll topnav__scroll--right"
              aria-label="Scroll right"
              tabIndex={canScrollRight ? 0 : -1}
              onClick={() => this.scrollBy(1)}
            >
              <i className="nc-icon nc-minimal-right" />
            </button>
          </nav>

          <div className="topnav__spacer" />

          <div className="topnav__actions">
            <button
              type="button"
              className="topnav__action"
              title={strings.lang}
              onClick={() => this.setLanguage(lang === "fr" ? "en" : "fr")}
            >
              <i className="nc-icon nc-world-2" />
              <span>{lang === "fr" ? "EN" : "FR"}</span>
            </button>
            <NotificationBell />
            <button
              type="button"
              className="topnav__action topnav__action--logout"
              onClick={this.handleLogout}
            >
              <i className="nc-icon nc-button-power" />
              <span>{strings.logout}</span>
            </button>
          </div>
        </div>

        {drawerOpen && this.renderDrawer(routes)}
        {openMenu !== null && (
          <div
            className="topnav__scrim"
            onClick={() => this.setState({ openMenu: null, menuPos: null })}
          />
        )}
      </header>
    );
  }
}

export default TopNav;
